/**
 * Pure logic for the Releases scene's announce-button state machine.
 *
 * The button has three modes — co-located here so they can be unit-tested
 * without rendering the scene (vitest in mops-web runs in `node`, not jsdom).
 *
 * - `announce`     — no MOPS draft is open; the operator can announce the
 *                    pre-release at the previewed head SHA.
 * - `repost-slack` — a MOPS draft is already open at the same SHA; clicking
 *                    re-runs the announce mutation, which the server treats
 *                    as idempotent (no second draft, but Slack re-posts).
 * - `disabled`     — a MOPS draft is open at a different SHA. The operator
 *                    must publish or discard the existing draft on github.com
 *                    before they can announce a fresh one.
 */
export type AnnounceButtonMode = 'announce' | 'repost-slack' | 'disabled';

export interface IAnnounceButtonState {
  mode: AnnounceButtonMode;
  label: string;
  /** Tooltip shown on the disabled state, naming the conflicting SHA. */
  tooltip?: string;
}

interface IDeriveAnnounceButtonStateInput {
  expectedHeadSha: string;
  draftTargetSha: string | null;
  /**
   * Slack channel name (with leading `#`) the announce mutation will post to,
   * threaded through here from the caller because mops-api gates the channel
   * on `IS_DEV` (`#production-deploys-test` in dev, `#production-deploys`
   * elsewhere) — keeping the gate in mops-api means the frontend never lies
   * about where the post is going. Plumbed as a parameter (rather than
   * imported from `env.ts`) so this module stays pure and trivially testable.
   */
  slackChannel: string;
}

export const SHORT_SHA_LENGTH = 7;
const shortSha = (sha: string) => sha.slice(0, SHORT_SHA_LENGTH);

/**
 * Bookkeeping for the "Posted at HH:MM — view in Slack" confirmation line
 * surfaced after a successful announce or re-post. `slackUrl` is null when
 * Slack was unreachable; the announce flow degrades to "draft created, no
 * permalink" rather than failing the whole mutation.
 */
export interface IAnnounceJustPosted {
  postedAt: Date;
  slackUrl: string | null;
}

/**
 * Compute the announce button's mode + label + tooltip from the preview's
 * head SHA and the open draft's target SHA (or `null` when no draft exists).
 */
export function deriveAnnounceButtonState({
  expectedHeadSha,
  draftTargetSha,
  slackChannel,
}: IDeriveAnnounceButtonStateInput): IAnnounceButtonState {
  const announceLabel = `Announce pre-release in ${slackChannel}`;
  if (draftTargetSha === null) {
    return {
      mode: 'announce',
      label: announceLabel,
    };
  }
  if (draftTargetSha === expectedHeadSha) {
    return {
      mode: 'repost-slack',
      label: 'Re-post Slack',
    };
  }
  return {
    mode: 'disabled',
    label: announceLabel,
    tooltip: `A draft for a different SHA (${shortSha(draftTargetSha)}) is already open — view it above.`,
  };
}

/**
 * Format a Date as `HH:MM` (24-hour) in the operator's local timezone — used
 * for the "Posted at HH:MM — view in Slack" confirmation line after announce.
 * Pinned to 24-hour so the inline string stays compact across locales (no
 * trailing AM/PM).
 */
export function formatPostedAtClockTime(timestamp: Date): string {
  return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * First non-empty line of a multi-line release body — used as the truncated
 * summary in the history table. Returns an empty string when the body has
 * no non-blank lines.
 */
export function summaryFirstLine(body: string): string {
  for (const line of body.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
}

/**
 * Maps server error codes thrown by `adminPublishReleaseDraft` and
 * `adminDeleteReleaseDraft` to operator-facing toast messages. The
 * server throws `Error('CODE: human description …')` per
 * `ReleasesController` — we match the prefix and pick a tight,
 * action-oriented message.
 *
 * Falls back to the raw error message for codes we don't recognize so
 * a forward-compat server change doesn't strand the operator with
 * "Something went wrong".
 */
export function mapDraftMutationError(message: string): string {
  if (message.includes('NO_DRAFT_TO_PUBLISH')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('NO_DRAFT_TO_DELETE')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('MULTIPLE_DRAFTS_FOUND')) {
    return 'Multiple drafts exist (race condition). Use Discard until one remains, then publish.';
  }
  if (message.includes('STALE_DRAFT_TAG')) {
    return 'Draft changed — refresh.';
  }
  if (message.includes('CI_NOT_GREEN')) {
    return 'CI must be green to publish.';
  }
  if (message.includes('RELEASE_TAG_RACE')) {
    return 'Tag was just created externally — refresh.';
  }
  if (message.includes('GITHUB_PUBLISH_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('GITHUB_DELETE_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('GITHUB_UPDATE_FAILED')) {
    return 'GitHub error — check Sentry.';
  }
  if (message.includes('NO_DRAFT_TO_UPDATE')) {
    return 'No open draft. Refresh.';
  }
  if (message.includes('STALE_CHECKLIST')) {
    return 'Checklist changed — refresh.';
  }
  return message;
}

/** True when the server error message indicates `MULTIPLE_DRAFTS_FOUND`. */
export function isMultipleDraftsError(message: string): boolean {
  return message.includes('MULTIPLE_DRAFTS_FOUND');
}

export const CHECKLIST_PREREQ_PREFIX = 'p:';
export const CHECKLIST_TEST_PLAN_PREFIX = 't:';

/**
 * Polling cadence for `adminGetCurrentReleaseDraft` — drives how quickly
 * one release manager sees another's checkbox toggles. Cheap (one GitHub
 * `listReleases` round-trip server-side); 30s feels current without
 * burning tokens. Tradeoff is documented in the persistence design doc.
 */
export const RELEASE_DRAFT_POLL_INTERVAL_MS = 30_000;

/**
 * Hex-encoded SHA-256 of the trimmed input. Server-side validation calls
 * `node:crypto` with the same algorithm; output is identical for
 * identical inputs. The toggle mutation sends this hash so the server
 * can detect that the client and the body agree on what the i-th
 * checklist line says.
 */
export async function sha256Hex(input: string): Promise<string> {
  const trimmed = input.trim();
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    /* istanbul ignore next */
    throw new Error('Web Crypto unavailable — cannot hash checklist line.');
  }
  const buffer = new TextEncoder().encode(trimmed);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const bytes = new Uint8Array(digest);
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

export interface IReleaseChecklistGateState {
  /** True when every item in both sections is checked. Always true when both lists are empty. */
  allChecked: boolean;
  /** Total number of items across both lists. Zero when both are empty. */
  totalCount: number;
  /** Number of items currently checked. */
  checkedCount: number;
  /** Total prerequisites (whether checked or not). */
  prereqTotalCount: number;
  /** Prerequisites still requiring review. */
  prereqUncheckedCount: number;
  /** Total test plan items (whether checked or not). */
  testPlanTotalCount: number;
  /** Test plan items still requiring completion. */
  testPlanUncheckedCount: number;
}

/** Stable key for a checklist item — `p:0`, `t:3`, etc. */
export function checklistKey(section: 'prereq' | 'testPlan', index: number): string {
  return `${section === 'prereq' ? CHECKLIST_PREREQ_PREFIX : CHECKLIST_TEST_PLAN_PREFIX}${index}`;
}

/**
 * Format a `Checked by **name** at M/D/YYYY @ H:MMam` marker for the
 * gate's optimistic overlay. Mirrors the server's `formatMarker` byte-for-
 * byte (Pacific Time, lowercase am/pm, zero-padded minute, no space
 * before am/pm) so the optimistic value is indistinguishable from what
 * the mutation response will write moments later — no flicker on
 * reconcile. The PT pin matches the release tool's audience (SF-based
 * release managers) and prevents the operator's browser zone from
 * showing one time while the server-written marker shows another.
 */
const PT_MARKER_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
export function formatChecklistMarker(operatorLocalPart: string, now: Date): string {
  const parts = PT_MARKER_FORMATTER.formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  const ampm = get('dayPeriod').toLowerCase();
  return `Checked by **${operatorLocalPart}** at ${get('month')}/${get('day')}/${get('year')} @ ${get('hour')}:${get('minute')}${ampm}`;
}

/**
 * Strips the LLM-generated `## Prerequisites` and
 * `## Pre-release test plan` sections from a release body. These
 * sections are surfaced as the interactive `ReleaseChecklistGate` when
 * a draft is open, so the static GitHub-style checkboxes inside the
 * body would be a visual duplicate.
 *
 * Each section runs from its `##` heading up to (but not including)
 * the next `##` heading or end of body. The trailing audit-footer
 * line stays intact because it isn't a `## …` heading. Heading text
 * is matched case-insensitively to tolerate minor LLM drift.
 *
 * Returns the body unchanged when neither section is present.
 */
export function stripChecklistSectionsFromBody(body: string): string {
  const sectionsToStripLower = new Set(['prerequisites', 'pre-release test plan']);
  const lines = body.split('\n');
  const kept: string[] = [];
  let skipping = false;

  for (const line of lines) {
    const headingMatch = /^##\s+(.+?)\s*$/.exec(line);
    if (headingMatch) {
      // New section starts — decide whether to skip it. The decision
      // re-evaluates on every `## …`, so consecutive stripped sections
      // and stripped→kept transitions both work.
      skipping = sectionsToStripLower.has(headingMatch[1].toLowerCase());
      if (skipping) {
        continue;
      }
    }
    if (!skipping) {
      kept.push(line);
    }
  }

  // Collapse any 3+ consecutive newlines left by the strip back down to two —
  // keeps the surrounding section spacing visually consistent.
  return kept.join('\n').replace(/\n{3,}/g, '\n\n');
}

/**
 * Pure aggregate state of the release checklist gate, given the source
 * lists and the set of currently-checked keys. Pulled out of
 * `ReleaseChecklistGate` so we can unit-test the state machine without
 * a DOM (the mops-web vitest config runs in `node`, not jsdom).
 *
 * Returns per-section uncheck counts so the disabled-Publish tooltip
 * can summarise progress as "x of y tests / a of b prerequisites" —
 * a more scannable signal than naming the next single item, which
 * scrolls past quickly in long releases.
 */
export function computeReleaseChecklistGateState(
  prereqTotalCount: number,
  testPlanTotalCount: number,
  checked: ReadonlySet<string>
): IReleaseChecklistGateState {
  let prereqCheckedCount = 0;
  for (let i = 0; i < prereqTotalCount; i += 1) {
    if (checked.has(checklistKey('prereq', i))) {
      prereqCheckedCount += 1;
    }
  }
  let testPlanCheckedCount = 0;
  for (let i = 0; i < testPlanTotalCount; i += 1) {
    if (checked.has(checklistKey('testPlan', i))) {
      testPlanCheckedCount += 1;
    }
  }

  const prereqUncheckedCount = prereqTotalCount - prereqCheckedCount;
  const testPlanUncheckedCount = testPlanTotalCount - testPlanCheckedCount;

  return {
    allChecked: prereqUncheckedCount === 0 && testPlanUncheckedCount === 0,
    totalCount: prereqTotalCount + testPlanTotalCount,
    checkedCount: prereqCheckedCount + testPlanCheckedCount,
    prereqTotalCount,
    prereqUncheckedCount,
    testPlanTotalCount,
    testPlanUncheckedCount,
  };
}

export type PublishButtonDisabledReason =
  | 'ci-not-green'
  | 'checklist-incomplete'
  | 'multiple-drafts'
  | 'preview-loading'
  | 'preview-error';

export interface IPublishButtonState {
  isEnabled: boolean;
  /**
   * Reason for the disabled state, when not enabled. Drives the tooltip
   * — `null` when the button is enabled (no tooltip needed). Order of
   * precedence (most-blocking first): preview-error → preview-loading
   * → multiple-drafts → ci-not-green → checklist-incomplete.
   */
  disabledReason: PublishButtonDisabledReason | null;
  /**
   * Human-readable tooltip for the disabled state — names the next
   * blocker so the operator knows what to fix. Empty string when the
   * button is enabled.
   */
  tooltip: string;
}

interface IDerivePublishButtonStateInput {
  ciConclusion: string;
  allChecklistItemsChecked: boolean;
  prereqUncheckedCount: number;
  testPlanUncheckedCount: number;
  multipleDraftsDetected: boolean;
  previewLoading: boolean;
  previewError: boolean;
}

/**
 * Computes the Publish button's enable/disable + tooltip state from the
 * full set of preconditions: CI green, checklist complete, no race
 * condition, preview data loaded. The tooltip names the most-blocking
 * issue so the operator knows exactly what to fix.
 */
export function derivePublishButtonState({
  ciConclusion,
  allChecklistItemsChecked,
  prereqUncheckedCount,
  testPlanUncheckedCount,
  multipleDraftsDetected,
  previewLoading,
  previewError,
}: IDerivePublishButtonStateInput): IPublishButtonState {
  if (previewError) {
    return {
      isEnabled: false,
      disabledReason: 'preview-error',
      tooltip: 'Preview failed to load — refresh.',
    };
  }
  if (previewLoading) {
    return {
      isEnabled: false,
      disabledReason: 'preview-loading',
      tooltip: 'Loading checklist…',
    };
  }
  if (multipleDraftsDetected) {
    return {
      isEnabled: false,
      disabledReason: 'multiple-drafts',
      tooltip: 'Multiple drafts exist — use Discard until one remains.',
    };
  }
  if (ciConclusion !== 'success') {
    return {
      isEnabled: false,
      disabledReason: 'ci-not-green',
      tooltip: 'CI must be green to publish.',
    };
  }
  if (!allChecklistItemsChecked) {
    // Summary form: "{x} tests require completion\n{y} prerequisites require
    // review". Each line appears only when that section has unchecked items
    // — a release with no prereqs shouldn't dangle a "0 prerequisites" line,
    // and a release where every prereq is checked drops to a single tests
    // line. Both lines being skipped is unreachable here:
    // `allChecklistItemsChecked` is false iff at least one uncheckedCount > 0.
    const lines: string[] = [];
    if (testPlanUncheckedCount > 0) {
      lines.push(`${testPlanUncheckedCount} tests require completion`);
    }
    if (prereqUncheckedCount > 0) {
      lines.push(`${prereqUncheckedCount} prerequisites require review`);
    }
    return {
      isEnabled: false,
      disabledReason: 'checklist-incomplete',
      tooltip: lines.join('\n'),
    };
  }
  return {
    isEnabled: true,
    disabledReason: null,
    tooltip: '',
  };
}
